import React, { useState, useEffect, useRef, useCallback, type ComponentType } from 'react';
import { useParams, useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { pickOpenRequestPage, withCurrentSearch } from '@/features/tenant-app/openRequestPage';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { componentMap as staticComponentMap } from '@/features/page-builder/componentMap';
import {
  enrichInventoryTableConfig,
  isGenericInventoryTableTitle,
  isInventoryLikeTableConfig,
  TABLE_COMPONENT_KIND_MAP,
} from '@/components/page-builder/lead-table/utils';
import { InventoryTablePageProvider } from '@/components/page-builder/lead-table/InventoryTablePageContext';
import {
  fetchPageConfig,
  fetchPagesForRole,
  getEffectiveToken,
} from '@/lib/auth/spoof';
import { useAuth } from '@/hooks/useAuth';

// Module-level cache to prevent duplicate page fetches across component remounts
const pageCache = new Map<string, { data: any; timestamp: number }>();
const PAGE_CACHE_TTL = 5000; // 5 seconds
const PAGE_CACHE_MAX = 8;

function setPageCache(key: string, value: { data: any; timestamp: number }) {
  const now = Date.now();
  for (const [k, v] of pageCache) {
    if (now - v.timestamp > PAGE_CACHE_TTL) pageCache.delete(k);
  }
  pageCache.set(key, value);
  while (pageCache.size > PAGE_CACHE_MAX) {
    const oldest = pageCache.keys().next().value;
    if (oldest === undefined) break;
    pageCache.delete(oldest);
  }
}

type ComponentMap = Record<string, ComponentType<any>>;

interface CustomAppOutletContext {
  tenantId: string | null;
  userRoleId: string | null;
  pages?: { id: string; name: string; display_order?: number; icon_name?: string }[];
  isUnmanndApp?: boolean;
}

const CustomAppPage: React.FC = () => {
  const { tenantSlug, pageId } = useParams<{ tenantSlug: string; pageId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const {
    tenantId: contextTenantId,
    userRoleId,
    pages: sidebarPages = [],
    isUnmanndApp = false,
  } = useOutletContext<CustomAppOutletContext>();
  const tenantId = contextTenantId ?? (typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null);
  
  const [page, setPage] = useState<{ name: string; config: any; header_title?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const componentMap = staticComponentMap as ComponentMap;
  const fetchingRef = useRef<string | null>(null);
  const redirectingRef = useRef(false);
  const sidebarPagesRef = useRef(sidebarPages);
  sidebarPagesRef.current = sidebarPages;

  const redirectToFirstSidebarPage = useCallback(async () => {
    if (redirectingRef.current || !tenantSlug) return;
    redirectingRef.current = true;

    const fromSidebar = pickOpenRequestPage(sidebarPagesRef.current, {
      search: location.search,
      excludeId: pageId,
    });
    if (fromSidebar?.id) {
      navigate(
        withCurrentSearch(`/app/${tenantSlug}/pages/${fromSidebar.id}`, location.search),
        { replace: true },
      );
      return;
    }

    try {
      if (!tenantId || !userRoleId) {
        navigate(withCurrentSearch(`/app/${tenantSlug}`, location.search), { replace: true });
        return;
      }
      const token = await getEffectiveToken(session?.access_token ?? null);
      let firstId: string | null = null;
      if (token) {
        const navPages = await fetchPagesForRole(tenantId, userRoleId, token);
        firstId =
          pickOpenRequestPage(navPages, { search: location.search, excludeId: pageId })?.id ??
          null;
      }
      if (!firstId) {
        const { data } = await supabase
          .from('pages')
          .select('id, name')
          .eq('tenant_id', tenantId)
          .eq('role', userRoleId)
          .eq('is_deleted', false)
          .order('display_order', { ascending: true });
        firstId =
          pickOpenRequestPage(data || [], { search: location.search, excludeId: pageId })?.id ??
          null;
      }
      if (firstId) {
        navigate(
          withCurrentSearch(`/app/${tenantSlug}/pages/${firstId}`, location.search),
          { replace: true },
        );
      } else {
        navigate(withCurrentSearch(`/app/${tenantSlug}`, location.search), { replace: true });
      }
    } catch {
      navigate(withCurrentSearch(`/app/${tenantSlug}`, location.search), { replace: true });
    }
  }, [tenantSlug, pageId, tenantId, userRoleId, session?.access_token, navigate, location.search]);

  // Supabase silently rotates session.access_token every so often, which recreates
  // redirectToFirstSidebarPage above. Keeping it out of the fetch effect's own deps
  // (via this ref) stops a routine token refresh from re-running the fetch and
  // flashing the whole page through "Loading page..." — it still always calls the
  // latest version when a redirect is actually needed.
  const redirectToFirstSidebarPageRef = useRef(redirectToFirstSidebarPage);
  redirectToFirstSidebarPageRef.current = redirectToFirstSidebarPage;

  useEffect(() => {
    if (!tenantId || !pageId) return;

    let isMounted = true;
    redirectingRef.current = false;

    // userRoleId is part of the key so a spoof/role switch on the same
    // pageId (see the effect's own deps below) can't serve a stale cache
    // entry fetched under the previous role
    const cacheKey = `${tenantId}-${pageId}-${userRoleId}`;
    const cached = pageCache.get(cacheKey);
    const now = Date.now();

    if (cached && now - cached.timestamp < PAGE_CACHE_TTL) {
      if (isMounted) {
        setPage(cached.data);
        setLoading(false);
        setError(null);
      }
      return;
    }

    if (fetchingRef.current === pageId) {
      return;
    }

    fetchingRef.current = pageId;
    if (isMounted) {
      setLoading(true);
      setError(null);
      setPage(null);
    }

    const spoofToken =
      typeof window !== 'undefined' ? window.localStorage.getItem('pyro_spoof_jwt') : null;

    const fetchPage = async () => {
      try {
        if (spoofToken && tenantId) {
          const pageData = await fetchPageConfig(pageId, tenantId, spoofToken);
          if (!isMounted) return;
          fetchingRef.current = null;
          if (pageData) {
            setPage(pageData);
            setPageCache(cacheKey, { data: pageData, timestamp: now });
            setLoading(false);
          } else {
            pageCache.delete(cacheKey);
            await redirectToFirstSidebarPageRef.current();
          }
          return;
        }

        const { data, error: fetchError } = await supabase
          .from('pages')
          .select('name, config, header_title')
          .eq('id', pageId)
          .eq('tenant_id', tenantId)
          .eq('is_deleted', false)
          .maybeSingle();

        if (!isMounted) return;
        fetchingRef.current = null;

        if (fetchError) {
          setError(fetchError.message);
          toast.error('Failed to load page');
          setLoading(false);
        } else if (data) {
          const pageData = {
            name: data.name,
            config: data.config,
            header_title: data.header_title,
          };
          setPage(pageData);
          setPageCache(cacheKey, { data: pageData, timestamp: now });
          setLoading(false);
        } else {
          pageCache.delete(cacheKey);
          await redirectToFirstSidebarPageRef.current();
        }
      } catch (err: any) {
        fetchingRef.current = null;
        if (!isMounted) return;
        if (err.name === 'AbortError' || err.message?.includes('aborted')) return;
        setError(err.message);
        setLoading(false);
      }
    };

    void fetchPage();

    return () => {
      isMounted = false;
      if (fetchingRef.current === pageId) {
        fetchingRef.current = null;
      }
    };
    // userRoleId is included deliberately: a spoof/role switch on the same
    // pageId must refetch (or at least re-check the now-role-scoped cache
    // above) rather than keep showing the previous role's page. This is
    // separate from session?.access_token, which stays excluded — a routine
    // Supabase token refresh must NOT re-run this effect (see the ref comment
    // above redirectToFirstSidebarPageRef), or every silent token rotation
    // would flash the page through "Loading page..." for no reason.
  }, [pageId, tenantId, userRoleId]);

  if (loading) return <div className="p-4">Loading page...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;
  if (!page) return <div className="p-4">Loading page...</div>;

  const effectivePageName = (
    sidebarPages.find((p) => p.id === pageId)?.name ||
    page.name ||
    ''
  ).trim();

  // Extract header title from page-level header_title or from header component in config
  const getHeaderTitle = () => {
    const pageName = effectivePageName;

    // First check page-level header_title
    if (page.header_title) {
      const header = page.header_title.trim();
      if (isGenericInventoryTableTitle(header) && pageName && !isGenericInventoryTableTitle(pageName)) {
        return pageName;
      }
      return header;
    }

    // Then check if there's a header component in the config
    if (Array.isArray(page.config)) {
      const headerComponent = page.config.find((comp: any) => comp.type === 'header');
      if (headerComponent?.config?.title) {
        const header = String(headerComponent.config.title).trim();
        if (isGenericInventoryTableTitle(header) && pageName && !isGenericInventoryTableTitle(pageName)) {
          return pageName;
        }
        return header;
      }
    }

    // Fallback to page name if no header title found
    return pageName || null;
  };

  const headerTitle = getHeaderTitle();
  const isRequestFormComponent = (type: string) =>
    type === 'inventoryRequestForm' || type === 'procurementRequestForm';

  const isInventoryTableComponent = (comp: { type?: string; config?: Record<string, unknown> }) => {
    const type = String(comp.type || '');
    // Request forms store inventory/unmannd entityType in config, but they are not tables.
    if (isRequestFormComponent(type)) return false;
    if (!isUnmanndApp) {
      return (
        Boolean(TABLE_COMPONENT_KIND_MAP[type]) ||
        (type === 'leadTable' && isInventoryLikeTableConfig(comp.config))
      );
    }
    return (
      Boolean(TABLE_COMPONENT_KIND_MAP[type]) ||
      type === 'leadTable' ||
      type === 'inventoryTable'
    );
  };

  const pageHasInventoryRequestTable =
    Array.isArray(page.config) && page.config.some((comp) => isInventoryTableComponent(comp));
  const pageHasRequestForm =
    Array.isArray(page.config) &&
    page.config.some((comp: { type?: string }) => isRequestFormComponent(String(comp.type || '')));
  const pageHasRecordsTable =
    Array.isArray(page.config) &&
    page.config.some((comp: { type?: string }) => {
      const t = String(comp.type || '');
      return (
        t === 'leadTable' ||
        t === 'oeLeadsTable' ||
        t === 'ticketTable' ||
        t === 'inventoryTable' ||
        Boolean(TABLE_COMPONENT_KIND_MAP[t])
      );
    });
  // Tables and the request form render their own title — hide sticky duplicate.
  // Dispatch widgets can also opt out (mobile-app setting).
  const hidePageHeader =
    pageHasInventoryRequestTable ||
    pageHasRecordsTable ||
    pageHasRequestForm ||
    (Array.isArray(page.config) &&
      page.config.some(
        (comp: { type?: string; config?: { hidePageHeader?: boolean } }) =>
          (comp.type === 'dispatchCardList' || comp.type === 'dispatchDashboard') &&
          comp.config?.hidePageHeader !== false
      ));

  return (
    <InventoryTablePageProvider pageName={effectivePageName} headerTitle={headerTitle ?? ''}>
    <div className={`w-full max-w-full min-w-0 ${isUnmanndApp ? 'flex flex-col' : ''}`}>
      {/* Sticky Header Title — mobile, tablet, and desktop */}
      {headerTitle && !hidePageHeader && (
        <div className="sticky top-0 z-40 w-full shrink-0 border-b border-gray-200 bg-white">
          {/* non-unmannd padding is tuned to vertically center the title
              against the sidebar's Pyro logo (see CustomAppLayout's
              pt-6 pb-4 logo row) — don't shrink this back to py-1.5 */}
          <div className={isUnmanndApp ? 'px-4 py-3' : 'px-4 pt-[36px] pb-[18px]'}>
            <h2
              className={
                isUnmanndApp
                  ? '!m-0 !text-[20px] !font-bold !leading-[1.15] !tracking-tight !uppercase text-[#0B1F4D] max-md:!text-[18px]'
                  : '!m-0 !text-lg !font-semibold !leading-snug text-gray-900 max-md:!text-base'
              }
              style={
                isUnmanndApp
                  ? { fontFamily: "Helvetica, 'Helvetica Neue', Arial, sans-serif" }
                  : undefined
              }
            >
              {headerTitle}
            </h2>
          </div>
        </div>
      )}
      
      {/* Page Content */}
      <div
        className={`w-full max-w-full min-w-0 ${
          isUnmanndApp ? 'flex flex-col' : ''
        }`}
      >
        <div
          className={`max-w-full min-w-0 ${
            isUnmanndApp
              ? 'px-2 pt-1 pb-2 flex flex-col'
              : 'pt-1'
          }`}
        >
          {Array.isArray(page.config)
            ? (page.config as any[]).map((component) => {
                const Renderer = componentMap[component.type];
                if (!Renderer) return null;
                // Skip header components if they exist in the config (we show it as fixed header above)
                if (component.type === 'header') return null;
                const tableConfig = enrichInventoryTableConfig(
                  String(component.type || ''),
                  effectivePageName,
                  component.config as Record<string, unknown> | undefined
                );
                return (
                  <div key={component.id}>
                    <Renderer {...component.props} config={tableConfig} />
                  </div>
                );
              })
            : null}
        </div>
      </div>
    </div>
    </InventoryTablePageProvider>
  );
};

export default CustomAppPage; 